import { inject, Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import type { KeycloakLoginOptions, KeycloakProfile } from 'keycloak-js';

/**
 * Thin adapter over the keycloak-js instance, preserving the small KeycloakService
 * API the application relied on. keycloak-angular v19+ removed its own KeycloakService
 * in favour of injecting the Keycloak instance directly (provideKeycloak()); this
 * adapter keeps that coupling in a single place so call sites stay unchanged.
 *
 * Mirrors ewsportal/src/app/auth/keycloak.service.ts (plus isUserInRole, which the
 * customerPortal uses).
 */
@Injectable({ providedIn: 'root' })
export class KeycloakService {
	private readonly keycloak = inject(Keycloak);

	getKeycloakInstance(): Keycloak {
		return this.keycloak;
	}

	getToken(): string {
		return this.keycloak.token ?? '';
	}

	isLoggedIn(): boolean {
		return !!this.keycloak.authenticated;
	}

	login(options?: KeycloakLoginOptions): Promise<void> {
		return this.keycloak.login(options);
	}

	logout(redirectUri?: string): Promise<void> {
		return this.keycloak.logout(redirectUri ? { redirectUri } : undefined);
	}

	loadUserProfile(): Promise<KeycloakProfile> {
		return this.keycloak.loadUserProfile();
	}

	updateToken(minValidity?: number): Promise<boolean> {
		return this.keycloak.updateToken(minValidity);
	}

	isUserInRole(role: string, resource?: string): boolean {
		return this.keycloak.hasRealmRole(role) || this.keycloak.hasResourceRole(role, resource);
	}
}
